<template>
  <div class="role-card">
    <ExBreadcrumbs 
      class="breadcrumbs-area" 
      :breadcrumbsList="computedBreadcrumbs"
      @onClickBreadcrumb="handleBreadcrumbClick"
    />
    <div class="role-header">
      <OrgObjectHeader
        type="Role"
        :status="roleData?.status || status"
        :code="roleData ? getOrgCodeName(roleData) : code"
        :name="roleData?.name || name"
        :archived="roleData?.archived ?? archived"
        :currentPosition="roleData"
        :dataStream="roleChatterData"
        :totalChatterCount="totalRoleChatterData"
        :chatterDrawerOpen="chatterDrawerOpen"
        @update:chatterDrawerOpen="chatterDrawerOpen = $event"
        @reloadChatterList="reloadChatterData"
        @requestMoreCheckpointsChatter="getChatterData"
        @optionClick="handleOptionClick"
      />
    </div>
    <RoleCardMenu
      :value="selectedTab"
      :active_responsibilities_count="roleData?.attachments_count?.responsibility ?? 0"
      :current_assignments_count="currentAssignmentsCount"
      @tab-change="onTabChange"
    />
    <div class="role-card-main-content">
      <v-tabs-items v-model="selectedTab" class="custom-tabs">
        <v-tab-item>
          <v-card elevation="0">
            <v-card-text class="custom-view purpose-view">
              <RolePurposeTab
                :value="roleData?.purpose || purpose"
                placeholder="Enter purpose for this role..."
                :maxLength="500"
                :isSaving="isSavingPurpose"
                @save="onPurposeSave"
                :hideAIButtons="true"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card elevation="0">
            <v-card-text>
              <RoleResponsibilitiesTab 
                ref="responsibilitiesTabRef" 
                :roleId="computedRoleId" 
                @roleDataUpdated="handleRoleDataUpdated"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
    
    <ActivateRole
      v-if="roleData"
      v-model="activateRoleDialog"
      @onSave="activateOrgRole"
      :role="roleData"
    />
    <RenameRole
      v-model="showRenameRoleDialog"
      @onSave="renameOrgRole"
      :role="roleData?.name || ''"
    />
    <DeleteRole
      v-if="roleData"
      v-model="deleteRoleDialog"
      @onDelete="deleteOrgRole($event)"
      :role="roleData || ''"
    />
    <RetiredRole
      v-if="roleData"
      v-model="retiredRoleDialog"
      @onSave="retiredOrgRole"
      :role="roleData"
    />
    <DeActivateRole
      v-if="roleData"
      v-model="deActivateRoleDialog"
      @onSave="deActivateOrgRole"
      :role="roleData"
    />
    <ReActivateRole
      v-if="roleData"
      v-model="reActivateRoleDialog"
      @onSave="reActivateOrgRole"
      :role="roleData"
    />
    <ArchivePopUp
      :action="archiveAction"
      type="role"
      :show="showArchiveDialog"
      :curretObj="roleData"
      @onClose="showArchiveDialog = false"
      @onArchive="onArchiveCatalogs"
    />
    <AttachedResponsibility
      v-model="attachResponsibilityDialog"
      @onSave="handleAttachResponsibility"
      :items="attachableResponsibilities"
      @item-selected="handleResponsibilitySelected"
      @add-item-selected="handleAddNewResponsibility"
      :parent-name="roleData?.name || name"
      :openDefault="openDefault"
      :type="'attach_responsibility_role_new_picker'"
      @load-more="loadMoreResponsibilities"
      @search="searchResponsibilities"
      :hasMoreData="hasMoreResponsibilitiesData"
      :isLoadingMore="isLoadingMoreResponsibilities"
      :isAssigning="isAssigningResponsibility"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, onMounted, getCurrentInstance } from 'vue';
import ExBreadcrumbs from '@components/breadcrumbs/ExBreadcrumbs.vue';
import OrgObjectHeader from '@components/OrgObjectHeader/OrgObjectHeader.vue';
import RoleCardMenu from '@components/RoleCardMenu/RoleCardMenu.vue';
import RolePurposeTab from '@components/RolePurposeTab/RolePurposeTab.vue';
import RoleResponsibilitiesTab from '@components/RoleResponsibilitiesTab/RoleResponsibilitiesTab.vue';
import ActivateRole from '@components/RolesCatalog/ActivateRole.vue';
import RetiredRole from '@components/RolesCatalog/RetiredRole.vue';
import DeleteRole from '@components/RolesCatalog/DeleteRole.vue';
import RenameRole from '@components/RolesCatalog/RenameRole.vue';
import DeActivateRole from '@components/RolesCatalog/DeActivateRole.vue';
import ReActivateRole from '@components/RolesCatalog/ReActivateRole.vue';
import ArchivePopUp from '@components/common/ArchivePopUp.vue';
import AttachedResponsibility from '@components/RolesCatalog/AttachedRole.vue';
import {
  getRole,
  updateRole,
  editRole,
  updateRetiredRole,
  updateDeActiveRole,
  updateReActiveRole,
  updateNameRole,
  deleteReadinessRole,
  deleteReadinessRoleWithResponsibilities,
  updateArchiveRole,
  updateUnArchiveRole,
  attachResponsibilityToRole,
  fetchAttachableResponsibilities,
  getChatterList
} from '@/api';

const props = defineProps({
  breadcrumbsList: {
    type: Array,
    default: () => []
  },
  status: {
    type: String,
    default: ''
  },
  code: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  archived: {
    type: Boolean,
    default: false
  },
  currentAssignmentsCount: {
    type: Number,
    default: 0
  },
  purpose: {
    type: String,
    default: ''
  },
  responsibilities: {
    type: Array,
    default: () => []
  },
  roleId: {
    type: [Number, String],
    default: null
  }
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;

const emit = defineEmits(['backToList', 'drilldown']);

const responsibilitiesTabRef = ref(null)
const selectedTab = ref(0);
const roleData = ref(null);
const accountId = ref(null);
const roleId = ref(null);
const roleChatterData = ref([]);
const totalRoleChatterData = ref(0);
const loadedPages = ref({});
const chatterDrawerOpen = ref(false);

const activateRoleDialog = ref(false);
const retiredRoleDialog = ref(false);
const deleteRoleDialog = ref(false);
const showRenameRoleDialog = ref(false);
const deActivateRoleDialog = ref(false);
const reActivateRoleDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref(null);
const attachResponsibilityDialog = ref(false);

const selectedResponsibilityId = ref(null);
const attachableResponsibilities = ref([]);
const openDefault = ref(false);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref('');
const responsibilitiesLimit = 10;

const isSavingPurpose = ref(false);

function onTabChange(tabIndex) {
  selectedTab.value = tabIndex;
  if (proxy.$router && route) {
    const roleTabMap = ['purpose', 'responsibilities', 'assignments'];
    const roleTabValue = roleTabMap[tabIndex] || 'purpose';
    
    proxy.$router.replace({
      ...route,
      query: {
        ...route.query,
        roleTab: roleTabValue
      }
    }).catch(() => {
    });
  }
}

function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === 'Positions') {
    if (proxy?.$router) {
      proxy.$router.push('/new-organization/positions').catch(() => {});
    }
  } else if (breadcrumb.positionId && !breadcrumb.disabled) {
    if (proxy?.$router) {
      proxy.$router.push({
        name: 'PositionDetailChild',
        params: { positionId: breadcrumb.positionId },
        query: { positionTab: 'attached-roles' }
      }).catch(() => {});
    }
  } else if (breadcrumb.text === 'Roles') {
    // Navigate back to roles list using router
    if (proxy?.$router) {
      proxy.$router.push('/new-organization/roles').catch(() => {});
    }
  }
}

const computedRoleId = computed(() => {
  return props.roleId || route?.params?.roleId || route?.query?.roleId || null;
});

const computedBreadcrumbs = computed(() => {
  if (props.breadcrumbsList && props.breadcrumbsList.length > 0) {
    return props.breadcrumbsList;
  }
  
  return [
    {
      text: "Roles",
      disabled: false,
      href: "/new-organization/roles",
    },
    {
      text: "Role",
      disabled: true,
    },
  ];
});

async function onPurposeSave(value) {
  try {
    isSavingPurpose.value = true;
    const idRole = roleData.value?.id;
    if (!idRole) {
      throw new Error('No role ID');
    }
    const payload = {
      purpose: value,
    };
    await editRole(accountId.value, idRole, payload);
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error updating purpose:', error);
  } finally {
    isSavingPurpose.value = false;
  }
}

function getOrgCodeName(currentRole) {
  if (currentRole?.code_str) {
    return currentRole.code_str.split(" ")[0];
  } else {
    return "#" + currentRole?.code;
  }
}

async function fetchRoleData() {
  try {
    if (accountId.value && roleId.value) {
      const { data } = await getRole(accountId.value, roleId.value);
      roleData.value = data;
    }
  } catch (error) {
    console.error('Error fetching role data:', error);
  }
}

function handleOptionClick(operation) {
  const action = operation.item?.value;
  
  switch (action) {
    case 'activate_action':
      activateRoleDialog.value = true;
      break;
    case 'retire_action':
      retiredRoleDialog.value = true;
      break;
    case 'reactivate_action':
      reActivateRoleDialog.value = true;
      break;
    case 'rename_action':
      showRenameRoleDialog.value = true;
      break;
    case 'delete_action':
      deleteRoleDialog.value = true;
      break;
    case 'deactivate_action':
      deActivateRoleDialog.value = true;
      break;
    case 'archive_action':
      archiveAction.value = 'archive';
      showArchiveDialog.value = true;
      break;
    case 'unarchive_action':
      archiveAction.value = 'unarchive';
      showArchiveDialog.value = true;
      break;
    case 'attach_responsibility':
      handleOpenAttachResponsibility();
      break;
    default:
      break;
  }
}

async function reloadChatterData() {
  if (!roleData.value) return;
  
  const currentRoleId = roleData.value.id;
  
  roleChatterData.value = [];
  totalRoleChatterData.value = 0;
  if (loadedPages.value[currentRoleId]) {
    loadedPages.value[currentRoleId].clear();
  }
  
  await getChatterData();
}

async function getChatterData() {
  if (!roleData.value) return;
  
  const currentRoleId = roleData.value.id;
  const currentLength = roleChatterData.value.length;
  const page = Math.floor(currentLength / 10) + 1;
  
  if (!loadedPages.value[currentRoleId]) {
    loadedPages.value[currentRoleId] = new Set();
  }
  
  if (loadedPages.value[currentRoleId].has(page)) {
    return;
  }
  
  try {
    const { data } = await getChatterList(accountId.value, currentRoleId, "role", page);
    loadedPages.value[currentRoleId].add(page);
    
    if (data && data.count) {
      totalRoleChatterData.value = data.count;
    }
    
    if (data && data.results) {
      let chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        let nameParts = el.recorded_by_name.split(" ");
        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };
        
        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }
        
        return newEl;
      });
      roleChatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error('Error fetching chatter data:', error);
  }
}

async function getRoleById(idRole) {
  try {
    const { data } = await getRole(accountId.value, idRole);
    roleData.value = data;
  } catch (error) {
    console.error('Error fetching role:', error);
  }
}

async function activateOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'active',
      account: accountId.value,
    };
    await updateRole(accountId.value, idRole, payload);
    activateRoleDialog.value = false;
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error activating role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function renameOrgRole(newName) {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    if (!idRole) {
      throw new Error('No role ID');
    }
    const payload = {
      name: newName,
    };
    await updateNameRole(accountId.value, idRole, payload);
    showRenameRoleDialog.value = false;
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error renaming role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function deleteOrgRole(deleteDraftResponsibilities) {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const payload = {
      delete_draft_responsibilities: deleteDraftResponsibilities,
    };
    if (deleteDraftResponsibilities) {
      await deleteReadinessRoleWithResponsibilities(accountId.value, idRole, payload);
    } else {
      await deleteReadinessRole(accountId.value, idRole);
    }
    deleteRoleDialog.value = false;
    
    // Navigate back to appropriate list based on route context
    if (proxy?.$router) {
      const routeName = route?.name;
      if (routeName === 'RoleDetailFromPosition' && route?.params?.positionId) {
        // If came from position, go back to position detail
        proxy.$router.push({
          name: 'PositionDetailChild',
          params: { positionId: route.params.positionId },
          query: { positionTab: 'attached-roles' }
        }).catch(() => {});
      } else {
        // Otherwise go back to roles list
        proxy.$router.push('/new-organization/roles').catch(() => {});
      }
    }
  } catch (error) {
    console.error('Error deleting role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function retiredOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'retired',
      account: accountId.value,
    };
    await updateRetiredRole(accountId.value, idRole, payload);
    retiredRoleDialog.value = false;
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error retiring role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function deActivateOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'inactive',
      account: accountId.value,
    };
    await updateDeActiveRole(accountId.value, idRole, payload);
    deActivateRoleDialog.value = false;
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error deactivating role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function reActivateOrgRole() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    const nameRole = roleData.value?.name;
    const payload = {
      name: nameRole,
      status: 'active',
      account: accountId.value,
    };
    await updateReActiveRole(accountId.value, idRole, payload);
    reActivateRoleDialog.value = false;
    await getRoleById(idRole);
  } catch (error) {
    console.error('Error reactivating role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function onArchiveCatalogs() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idRole = roleData.value?.id;
    
    if (archiveAction.value === 'archive') {
      await updateArchiveRole(accountId.value, 'role', idRole);
      await getRoleById(idRole);
    } else if (archiveAction.value === 'unarchive') {
      await updateUnArchiveRole(accountId.value, 'role', idRole);
      await getRoleById(idRole);
    }
    
    showArchiveDialog.value = false;
    archiveAction.value = null;
  } catch (error) {
    console.error('Error archiving/unarchiving role:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleOpenAttachResponsibility() {
  attachResponsibilityDialog.value = true;
  openDefault.value = true;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = '';
  await fetchAttachableResponsibilitiesList();
}

async function fetchAttachableResponsibilitiesList(isLoadMore = false) {
  try {
    if (isLoadMore) {
      isLoadingMoreResponsibilities.value = true;
    }
    
    const offset = (responsibilitiesPage.value - 1) * responsibilitiesLimit;
    const response = await fetchAttachableResponsibilities(
      accountId.value,
      roleId.value,
      responsibilitiesLimit,
      offset,
      responsibilitiesSearchTerm.value
    );
    
    const newItems = (response.data.results || []).map((item) => ({
      id: item.id,
      code: item.code_str.match(/\d+/)[0],
      name: item.name,
      status: item.status,
      attached: item?.attached_to_role
    }));
    
    if (isLoadMore) {
      attachableResponsibilities.value.push(...newItems);
    } else {
      attachableResponsibilities.value = newItems;
    }
    
    hasMoreResponsibilitiesData.value = !!response.data.next;
  } catch (error) {
    console.error('Error fetching attachable responsibilities:', error);
    attachableResponsibilities.value = [];
    hasMoreResponsibilitiesData.value = false;
  } finally {
    if (isLoadMore) {
      isLoadingMoreResponsibilities.value = false;
    }
  }
}

function handleResponsibilitySelected(responsibility) {
  if (!responsibility || !responsibility.id) return;
  selectedResponsibilityId.value = responsibility.id;
}

async function handleAddNewResponsibility(responsibilityData) {
  try {
    proxy.$loader?.setAppLoading(true);
    const newResponsibility = await proxy.$store?.dispatch(
      'readinessCatalogsStore/createResponsibility',
      { name: responsibilityData.name, accountId: accountId.value }
    );
    
    if (newResponsibility && newResponsibility.data && newResponsibility.data.id) {
      selectedResponsibilityId.value = newResponsibility.data.id;
      await handleAttachResponsibility();
    }
  } catch (error) {
    console.error('Error creating and attaching responsibility:', error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleAttachResponsibility() {
  if (!selectedResponsibilityId.value) return;
  
  try {
    isAssigningResponsibility.value = true;
    proxy.$loader?.setAppLoading(true);
    
    const payload = {
      attachment_id: selectedResponsibilityId.value,
    };
    
    await attachResponsibilityToRole(accountId.value, roleId.value, payload);
    
    attachResponsibilityDialog.value = false;
    selectedResponsibilityId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = '';
    
    await getRoleById(roleId.value);
    await responsibilitiesTabRef.value?.fetchResponsibilities();
    
  } catch (error) {
    console.error('Error attaching responsibility:', error);
  } finally {
    isAssigningResponsibility.value = false;
    proxy.$loader?.setAppLoading(false);
  }
}

async function loadMoreResponsibilities() {
  responsibilitiesPage.value += 1;
  await fetchAttachableResponsibilitiesList(true);
}

async function searchResponsibilities(searchTerm) {
  responsibilitiesSearchTerm.value = searchTerm;
  responsibilitiesPage.value = 1;
  await fetchAttachableResponsibilitiesList(false);
}

async function handleRoleDataUpdated() {
  // Refresh roleData when child component notifies of updates
  if (roleId.value) {
    await getRoleById(roleId.value);
  }
}

onMounted(async () => {
  try {
    proxy.$loader?.show();
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    roleId.value = computedRoleId.value;
    
    const roleTabFromQuery = route?.query?.roleTab;
    if (roleTabFromQuery) {
      const roleTabMap = { 'purpose': 0, 'responsibilities': 1, 'assignments': 2 };
      const tabIndex = roleTabMap[roleTabFromQuery.toLowerCase()];
      if (tabIndex !== undefined) {
        selectedTab.value = tabIndex;
      }
    }
    
    await fetchRoleData();
  } finally {
    proxy.$loader?.hide();
  }
});
</script>

<style scoped>
.role-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A;
  border: 1px solid #EAECF0;
  width: 100%;
  padding: 24px 12px 12px 12px;
}
.role-header {
  padding: 10px 0px 0px 0px;
}
.breadcrumbs-area {
  padding: 10px 0px 0px 5px !important;
}
.role-card-main-content {
  min-height: 400px;
  border-radius: 0px 0px 7px 7px;
}
::v-deep .role-header .header-header-container {
  position: relative;
  left: -10px;
}
::v-deep .role-header .header-org-menu {
  right: 0px !important;
}
::v-deep .role-header .header-org-menu .header-chatter-type-icon {
  padding: 2px;
  left: 8px;
}

::v-deep .filter-input-search {
  min-width: auto !important;
}

::v-deep .search-button {
  right: -22px !important;
}

::v-deep .search-bar-wrapper {
  align-items: center !important;
}

::v-deep .header-chatter-icon {
  margin-right: 7px !important;
}

::v-deep .header-org-menu {
  margin-right: 25px !important;
}

::v-deep .purpose-view {
  padding: 100%;
}

::v-deep .role-responsibilities-tab {
  box-shadow: none;
}
</style>
